import { useState } from 'react'
import type { AccountTeam } from '../../pages/admin/accountStore'
import { WEEKDAY_LABELS, type ShiftTemplate, type ShiftTemplateValues } from '../../pages/admin/catalogStore'

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

// Minutes from the start to the end going forward, so a shift that ends after
// midnight is measured across it.
function lengthMinutes(startTime: string, endTime: string): number {
  const [startHour, startMinute] = startTime.split(':').map(Number)
  const [endHour, endMinute] = endTime.split(':').map(Number)
  const length = endHour * 60 + endMinute - (startHour * 60 + startMinute)
  return length <= 0 ? length + 1440 : length
}

function lengthLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

/**
 * A shift template: its hours, meal break, the days it runs, and the team it
 * belongs to, or the whole office. A shift may end after midnight. Used to
 * create a template and to change one.
 */
export default function ShiftTemplateForm({
  template,
  teams,
  saving,
  error,
  onSave,
  onCancel,
}: {
  template?: ShiftTemplate
  teams: AccountTeam[]
  saving: boolean
  error: string | null
  onSave: (values: ShiftTemplateValues) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(template?.name ?? '')
  const [startTime, setStartTime] = useState(template?.startTime ?? '07:00')
  const [endTime, setEndTime] = useState(template?.endTime ?? '15:00')
  const [mealMinutes, setMealMinutes] = useState(String(template?.mealMinutes ?? 0))
  const [weekdays, setWeekdays] = useState<Set<number>>(new Set(template?.weekdays ?? [1, 2, 3, 4, 5]))
  const [teamId, setTeamId] = useState(template?.teamId ?? '')

  const length = startTime && endTime && startTime !== endTime ? lengthMinutes(startTime, endTime) : 0
  const meal = mealMinutes === '' ? NaN : Number(mealMinutes)
  const mealOk = Number.isInteger(meal) && meal >= 0 && meal < length
  const canSave = name.trim() !== '' && length > 0 && mealOk && weekdays.size > 0
  const used = (template?.placements ?? 0) + (template?.targets ?? 0) > 0

  function toggleDay(day: number) {
    setWeekdays((current) => {
      const next = new Set(current)
      if (next.has(day)) next.delete(day)
      else next.add(day)
      return next
    })
  }

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label={template ? `Edit ${template.name}` : 'Create shift template'}
      onSubmit={(event) => {
        event.preventDefault()
        if (!canSave || saving) return
        onSave({
          name: name.trim(),
          startTime,
          endTime,
          mealMinutes: meal,
          weekdays: [...weekdays].sort((a, b) => a - b),
          teamId: teamId || null,
        })
      }}
    >
      {template && used && (
        <p className="text-small text-ink-muted">
          {template.placements} {template.placements === 1 ? 'placement' : 'placements'} and {template.targets} staffing{' '}
          {template.targets === 1 ? 'target' : 'targets'} use this shift.
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2">
          <label htmlFor="shift-name" className={LABEL}>
            Name
          </label>
          <input id="shift-name" type="text" maxLength={100} value={name} onChange={(event) => setName(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="shift-start" className={LABEL}>
            Starts
          </label>
          <input id="shift-start" type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="shift-end" className={LABEL}>
            Ends
          </label>
          <input id="shift-end" type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="shift-meal" className={LABEL}>
            Meal break (minutes)
          </label>
          <input
            id="shift-meal"
            type="number"
            min={0}
            step={1}
            value={mealMinutes}
            onChange={(event) => setMealMinutes(event.target.value)}
            aria-describedby="shift-length"
            className={FIELD}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="shift-team" className={LABEL}>
            Team
          </label>
          <select id="shift-team" value={teamId} onChange={(event) => setTeamId(event.target.value)} className={FIELD}>
            <option value="">Office-wide</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
        <p id="shift-length" role="status" className="self-end pb-2 text-small text-ink-muted sm:col-span-1">
          {length === 0
            ? 'Start and end must differ.'
            : !mealOk
              ? 'The meal break must be shorter than the shift.'
              : `Runs ${lengthLabel(length)}${length > 0 && startTime > endTime ? ', ending the next day' : ''}.`}
        </p>
      </div>

      <fieldset>
        <legend className={LABEL}>Runs on</legend>
        <div className="flex flex-wrap gap-x-4">
          {WEEKDAY_LABELS.map((label, day) => (
            <label key={label} className="flex min-h-11 items-center gap-2 text-small">
              <input type="checkbox" checked={weekdays.has(day)} onChange={() => toggleDay(day)} className="size-5" />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Saving…' : template ? 'Save changes' : 'Create shift template'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
